import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';
import Navbar from './Navbar';

// Resolves a usable <img> src from either a full URL or a relative storage path.
// Same helper pattern as JobSearch.jsx.
const resolveLogoSrc = (logo) => {
  if (!logo) return null;
  return logo.startsWith('http') ? logo : `${BACKEND_URL}/storage/${logo}`;
};

function formatSalary(min, max, currency) {
  if (!min && !max) return null;
  const symbol = currency === 'INR' ? '₹' : (currency ? `${currency} ` : '');
  const fmt = (n) => Math.round(n / 1000) + 'K';
  if (min && max) return `${symbol}${fmt(min)} - ${symbol}${fmt(max)}`;
  return `${symbol}${fmt(min || max)}`;
}

function formatExperience(min, max) {
  if (min == null && max == null) return null;
  if (min != null && max != null) return `${min}-${max} yrs`;
  return `${min ?? max}+ yrs`;
}

// Small match-score pill — same colour thresholds as JobSearch.jsx's
// MatchRing (>=80 green, >=50 blue, else amber), just a flat badge instead
// of an SVG ring since aggregated job cards are more compact.
function MatchBadge({ score }) {
  const color = score >= 80 ? '#10B981' : score >= 50 ? '#2563EB' : '#F59E0B';
  return (
    <span
      className="text-[11px] font-bold px-2 py-0.5 rounded-full text-white shrink-0 whitespace-nowrap"
      style={{ backgroundColor: color }}
    >
      {score}% match
    </span>
  );
}

function AggregatedJobList() {
  const [searchParams] = useSearchParams();
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    keyword: searchParams.get('keyword') || '',
    location: searchParams.get('location') || '',
  });

  const [matchScores, setMatchScores] = useState({}); // { [jobId]: score }
  const [scoring, setScoring] = useState(false);
const [applying, setApplying] = useState({}); // { [jobId]: 'idle' | 'loading' | 'applied' }


  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.keys(filters).forEach((key) => {
        if (filters[key]) params[key] = filters[key];
      });
      const response = await api.get('/aggregated-jobs', { params });
      setJobs(response.data);
    } catch (err) {
      console.error('Failed to fetch aggregated jobs', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Match scoring — only for logged-in job seekers, only once jobs are
  // loaded. Unlike JobSearch.jsx, no client-side profile assembly is
  // needed: the backend's aggregatedScore() builds the candidate profile
  // itself from the authenticated user, so this just sends job IDs.
  useEffect(() => {
    const fetchMatchScores = async () => {
      if (!user || user.role !== 'job_seeker' || jobs.length === 0) return;
      const token = localStorage.getItem('token');
      if (!token) return;

      setScoring(true);
      try {
        const res = await api.post('/aggregated-jobs/match-score', {
          jobs: jobs.map((j) => j.id),
        });
        const map = {};
        res.data.forEach((s) => { map[s.id] = s.score; });
        setMatchScores(map);
      } catch (err) {
        console.error('Match scoring failed', err);
      } finally {
        setScoring(false);
      }
    };
    fetchMatchScores();
  }, [jobs, user]);



  const handleApply = async (e, jobId) => {
  e.preventDefault();
  e.stopPropagation();

  if (!user) {
    window.location.href = '/login';
    return;
  }
  if (user.role !== 'job_seeker') return;

  setApplying((prev) => ({ ...prev, [jobId]: 'loading' }));
  try {
    await api.post(`/aggregated-jobs/${jobId}/apply`);
    setApplying((prev) => ({ ...prev, [jobId]: 'applied' }));
  } catch (err) {
    if (err?.response?.status === 409) {
      setApplying((prev) => ({ ...prev, [jobId]: 'applied' }));
    } else {
      console.error('Apply failed', err);
      setApplying((prev) => ({ ...prev, [jobId]: 'idle' }));
    }
  }
};


  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchJobs();
  };

  return (
    <div className="min-h-screen bg-[#EFF6FF]">
      <Navbar user={user} />

      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        <h2 className="text-xl sm:text-2xl font-bold text-[#0D1B3D] mb-1">
          {jobs.length} jobs from partner sources
        </h2>
        <p className="text-gray-500 text-sm mb-6">
          {filters.location ? `Jobs near ${filters.location}` : 'Aggregated from external job sources'}
        </p>

        {scoring && (
          <p className="text-xs text-[#2563EB] mb-4">Scoring jobs against your profile…</p>
        )}

        {/* Simple search bar — keyword + location only, matching the
            aggregated job model's smaller field set (no job_type/work_mode
            columns on aggregated_jobs). */}
        <form onSubmit={handleSearch} className="bg-white rounded-xl shadow p-4 mb-6 flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            name="keyword"
            placeholder="Job title, skills"
            value={filters.keyword}
            onChange={handleFilterChange}
            className="flex-1 border rounded-lg px-3 py-2 text-sm"
          />
          <input
            type="text"
            name="location"
            placeholder="City"
            value={filters.location}
            onChange={handleFilterChange}
            className="flex-1 border rounded-lg px-3 py-2 text-sm"
          />
          <button
            type="submit"
            className="bg-[#2563EB] text-white px-5 py-2 rounded-lg font-semibold text-sm hover:bg-blue-700"
          >
            Search
          </button>
        </form>

        <div className="space-y-3">
          {loading ? (
            <p className="text-gray-500">Loading jobs...</p>
          ) : jobs.length === 0 ? (
            <div className="bg-white rounded-xl shadow p-10 text-center text-gray-500">
              No jobs found matching your criteria.
            </div>
          ) : (
            jobs.map((job) => {
              const logoSrc = resolveLogoSrc(job.company_logo);
              const salary = formatSalary(job.salary_min, job.salary_max, job.salary_currency);
              const experience = formatExperience(job.experience_min, job.experience_max);
              const matchScore = matchScores[job.id];

              return (
                <Link
                  key={job.id}
                  to={`/aggregated-jobs/${job.id}`}
                  className="block bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-3 sm:p-4"
                >
                  <div className="flex items-start gap-3">
                    {logoSrc ? (
                      <img src={logoSrc} alt="" className="w-9 h-9 rounded-full object-cover shrink-0 border border-gray-100" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-blue-500 flex items-center justify-center font-bold text-white shrink-0">
                        {job.company_name?.charAt(0)?.toUpperCase() || 'J'}
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="font-bold text-gray-900 text-sm truncate">{job.title}</h3>
                          <p className="text-xs text-gray-500 truncate">
                            {job.company_name}
                            {job.location && <span> · {job.location}</span>}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          {matchScore != null && <MatchBadge score={matchScore} />}
                          {salary && (
                            <span className="text-sm font-bold text-gray-900 whitespace-nowrap">
                              {salary}
                            </span>
                          )}
                        </div>
                      </div>

                      <p className="text-xs text-gray-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                        {job.job_type && <span>{job.job_type.replace(/_/g, ' ')}</span>}
                        {experience && <span>· {experience}</span>}
                        {job.category && <span>· {job.category}</span>}
                      </p>

                     <div className="flex items-center justify-between mt-2">
  {job.source_name ? (
    <p className="text-[11px] text-gray-400">Source: {job.source_name}</p>
  ) : <span />}

  <button
    onClick={(e) => handleApply(e, job.id)}
    disabled={applying[job.id] === 'loading' || applying[job.id] === 'applied'}
    className={`text-xs font-semibold px-4 py-1.5 rounded-lg transition-colors shrink-0 ${
      applying[job.id] === 'applied'
        ? 'bg-green-50 text-green-600 cursor-default'
        : 'bg-[#2563EB] text-white hover:bg-blue-700'
    }`}
  >
    {applying[job.id] === 'applied' ? 'Applied ✓' : applying[job.id] === 'loading' ? 'Applying…' : 'Apply'}
  </button>
</div>
                    </div>
                  </div>
                </Link>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default AggregatedJobList;